import { useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Space,
  Tag,
  Popconfirm,
  message,
  Card,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, CalendarOutlined, SearchOutlined } from "@ant-design/icons";
import Header from "../_shared/components/Header";
import MachineDetail from "./MachineDetail";
import {
  useGetSchedulingMachinesQuery,
  useCreateMachineMutation,
  useUpdateMachineMutation,
  useDeleteMachineMutation,
  useGetMachineGroupsQuery,
  useCreateMachineGroupMutation,
  useUpdateMachineGroupMutation,
  useDeleteMachineGroupMutation,
  useGetTasksQuery,
  useUpdateTaskMutation,
} from "../../../redux/api/scheduling";

type BulkField = "attended_efficiency" | "unattended_efficiency" | "max_unattended_hours";

const BULK_FIELD_LABELS: Record<BulkField, string> = {
  attended_efficiency: "Attended Efficiency (%)",
  unattended_efficiency: "Unattended Efficiency (%)",
  max_unattended_hours: "Max Unattended Hours",
};

export default function Machines() {
  const { data, isLoading, refetch } = useGetSchedulingMachinesQuery({});
  const { data: tasksData } = useGetTasksQuery({});
  const [createMachine] = useCreateMachineMutation();
  const [updateMachine] = useUpdateMachineMutation();
  const [updateTask] = useUpdateTaskMutation();
  const [deleteMachine] = useDeleteMachineMutation();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [selectedMachineId, setSelectedMachineId] = useState<number | null>(null);
  const [form] = Form.useForm();
  const [postCreateId, setPostCreateId] = useState<number | null>(null);
  const [associateTasksOpen, setAssociateTasksOpen] = useState(false);
  const [selectedTaskIds, setSelectedTaskIds] = useState<number[]>([]);
  const [associating, setAssociating] = useState(false);
  const [searchText, setSearchText] = useState("");

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [bulkField, setBulkField] = useState<BulkField | null>(null);
  const [bulkValue, setBulkValue] = useState<number | null>(null);
  const [bulkLoading, setBulkLoading] = useState(false);

  const { data: groupsData, refetch: refetchGroups } = useGetMachineGroupsQuery({});
  const [createGroup] = useCreateMachineGroupMutation();
  const [updateGroup] = useUpdateMachineGroupMutation();
  const [deleteGroup] = useDeleteMachineGroupMutation();
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState<number | null>(null);
  const [groupForm] = Form.useForm();

  const machines = (data?.results || data || []).filter((m: any) =>
    searchText ? m.name?.toLowerCase().includes(searchText.toLowerCase()) : true
  );
  const groups = groupsData?.results || groupsData || [];
  const allTasks: any[] = tasksData?.results || tasksData || [];

  const handleSubmit = async (values: any) => {
    try {
      if (editingId) {
        await updateMachine({ id: editingId, ...values }).unwrap();
        message.success("Machine updated");
        setModalOpen(false);
        form.resetFields();
        setEditingId(null);
        refetch();
      } else {
        const result = await createMachine(values).unwrap();
        message.success("Machine created");
        setModalOpen(false);
        form.resetFields();
        setEditingId(null);
        setPostCreateId(result.id);
        refetch();
      }
    } catch (err: any) {
      message.error(err?.data?.detail || err?.data?.name?.[0] || "Error saving machine");
    }
  };

  const handleEdit = (record: any) => {
    setEditingId(record.id);
    form.setFieldsValue(record);
    setModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteMachine(id).unwrap();
      message.success("Machine deleted");
      refetch();
    } catch (err: any) {
      message.error(err?.data?.detail || "Error deleting machine");
    }
  };

  const openBulkModal = (field: BulkField) => {
    if (selectedRowKeys.length === 0) {
      message.warning("Select at least one machine first");
      return;
    }
    setBulkField(field);
    setBulkValue(null);
  };

  const handleBulkApply = async () => {
    if (bulkField === null || bulkValue === null) return;
    setBulkLoading(true);
    try {
      await Promise.all(
        selectedRowKeys.map((id) =>
          updateMachine({ id: Number(id), [bulkField]: bulkValue }).unwrap()
        )
      );
      message.success(
        `Updated ${BULK_FIELD_LABELS[bulkField]} to ${bulkValue}${bulkField !== "max_unattended_hours" ? "%" : "h"} on ${selectedRowKeys.length} machine(s)`
      );
      setBulkField(null);
      setBulkValue(null);
      setSelectedRowKeys([]);
      refetch();
    } catch {
      message.error("Failed to update some machines");
    } finally {
      setBulkLoading(false);
    }
  };

  const handleGroupSubmit = async (values: any) => {
    try {
      if (editingGroupId) {
        await updateGroup({ id: editingGroupId, ...values }).unwrap();
        message.success("Group updated");
      } else {
        await createGroup(values).unwrap();
        message.success("Group created");
      }
      setGroupModalOpen(false);
      groupForm.resetFields();
      setEditingGroupId(null);
      refetchGroups();
      refetch();
    } catch (err: any) {
      message.error(err?.data?.detail || "Error saving group");
    }
  };

  const handleEditGroup = (record: any) => {
    setEditingGroupId(record.id);
    groupForm.setFieldsValue({
      name: record.name,
      description: record.description,
      machines: (record.machines_detail || []).map((m: any) => m.id),
    });
    setGroupModalOpen(true);
  };

  const handleDeleteGroup = async (id: number) => {
    try {
      await deleteGroup(id).unwrap();
      message.success("Group deleted");
      refetchGroups();
      refetch();
    } catch (err: any) {
      message.error(err?.data?.detail || "Error deleting group");
    }
  };

  const allMachines = data?.results || data || [];
  const machineOptions = allMachines.map((m: any) => ({ value: m.id, label: m.name }));

  const groupColumns = [
    { title: "Name", dataIndex: "name", key: "name", sorter: (a: any, b: any) => a.name.localeCompare(b.name) },
    {
      title: "Machines",
      key: "machines",
      render: (_: any, record: any) => (
        <Space size={[4, 4]} wrap>
          {(record.machines_detail || []).map((m: any) => (
            <Tag key={m.id}>{m.name}</Tag>
          ))}
        </Space>
      ),
    },
    { title: "Description", dataIndex: "description", key: "description", ellipsis: true },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_: any, record: any) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={() => handleEditGroup(record)} />
          <Popconfirm title="Delete this group?" onConfirm={() => handleDeleteGroup(record.id)}>
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a: any, b: any) => (a.name || "").localeCompare(b.name || ""),
    },
    {
      title: "Attended Eff.",
      dataIndex: "attended_efficiency",
      key: "attended_efficiency",
      sorter: (a: any, b: any) => Number(a.attended_efficiency) - Number(b.attended_efficiency),
      render: (v: string) => v ? `${v}%` : "—",
    },
    {
      title: "Unattended Eff.",
      dataIndex: "unattended_efficiency",
      key: "unattended_efficiency",
      sorter: (a: any, b: any) => Number(a.unattended_efficiency) - Number(b.unattended_efficiency),
      render: (v: string) => v ? `${v}%` : "—",
    },
    {
      title: "Max Unattended (hrs)",
      dataIndex: "max_unattended_hours",
      key: "max_unattended_hours",
      sorter: (a: any, b: any) => Number(a.max_unattended_hours) - Number(b.max_unattended_hours),
      render: (v: string) => v ?? "—",
    },
    {
      title: "Groups",
      key: "groups",
      render: (_: any, record: any) => {
        const grps = record.groups || [];
        return grps.length > 0 ? (
          <Space size={[4, 4]} wrap>
            {grps.map((g: any) => <Tag key={g.id} color="blue">{g.name}</Tag>)}
          </Space>
        ) : <span className="text-gray-400 text-xs">—</span>;
      },
    },
    {
      title: "Active",
      dataIndex: "is_active",
      key: "is_active",
      sorter: (a: any, b: any) => Number(a.is_active) - Number(b.is_active),
      render: (v: boolean) => (v ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: any) => (
        <Space>
          <Button
            icon={<CalendarOutlined />}
            size="small"
            onClick={() => setSelectedMachineId(record.id)}
          >
            Schedule
          </Button>
          <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)} />
          <Popconfirm title="Delete this machine?" onConfirm={() => handleDelete(record.id)}>
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (selectedMachineId) {
    return (
      <div className="w-full p-4">
        <Button onClick={() => setSelectedMachineId(null)} className="mb-4">
          ← Back to Machines
        </Button>
        <MachineDetail machineId={selectedMachineId} />
      </div>
    );
  }

  return (
    <div className="w-full p-4" style={{ marginTop: 0 }}>
      {/* Machine creation lives on the /machines page (ERP source of truth).
          This page manages scheduling config (efficiency, groups, schedule). */}
      <Header title="Machines (Scheduling)" />

      <div className="mb-3 flex items-center gap-2 flex-wrap">
        <Input
          placeholder="Search machines..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
          style={{ width: 220 }}
          size="small"
        />
        <span className="text-sm text-gray-500 ml-2 mr-1">
          Bulk update ({selectedRowKeys.length} selected):
        </span>
        <Button size="small" disabled={selectedRowKeys.length === 0} onClick={() => openBulkModal("attended_efficiency")}>
          Attended Eff.
        </Button>
        <Button size="small" disabled={selectedRowKeys.length === 0} onClick={() => openBulkModal("unattended_efficiency")}>
          Unattended Eff.
        </Button>
        <Button size="small" disabled={selectedRowKeys.length === 0} onClick={() => openBulkModal("max_unattended_hours")}>
          Max Unattended Hrs
        </Button>
      </div>

      <Table
        dataSource={machines}
        columns={columns}
        loading={isLoading}
        rowKey="id"
        size="small"
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys),
        }}
      />

      {/* Machine Groups Section */}
      <Card
        title="Machine Groups"
        className="mt-6 mb-4"
        extra={
          <Button
            size="small"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingGroupId(null);
              groupForm.resetFields();
              setGroupModalOpen(true);
            }}
          >
            Add Group
          </Button>
        }
      >
        <Table
          dataSource={groups}
          columns={groupColumns}
          rowKey="id"
          size="small"
          pagination={false}
        />
      </Card>

      {/* Add / Edit Machine Group Modal */}
      <Modal
        title={editingGroupId ? "Edit Machine Group" : "Add Machine Group"}
        open={groupModalOpen}
        onCancel={() => { setGroupModalOpen(false); setEditingGroupId(null); }}
        onOk={() => groupForm.submit()}
        width={500}
        destroyOnClose
      >
        <Form form={groupForm} layout="vertical" onFinish={handleGroupSubmit}>
          <Form.Item name="name" label="Group Name" rules={[{ required: true }]}>
            <Input placeholder="e.g. CNC Mills" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="machines" label="Machines">
            <Select
              mode="multiple"
              options={machineOptions}
              placeholder="Select machines..."
              showSearch
              filterOption={(input, option) =>
                (option?.label?.toString() || "").toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Add / Edit Machine Modal */}
      <Modal
        title={editingId ? "Edit Machine" : "Add Machine"}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        width={500}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="attended_efficiency" label="Attended Efficiency (%)" initialValue={75}>
            <InputNumber min={0} max={100} step={5} />
          </Form.Item>
          <Form.Item name="unattended_efficiency" label="Unattended Efficiency (%)" initialValue={50}>
            <InputNumber min={0} max={100} step={5} />
          </Form.Item>
          <Form.Item name="max_unattended_hours" label="Max Unattended Hours" initialValue={16}>
            <InputNumber min={0} max={168} step={1} />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* Post-Create Prompt */}
      <Modal
        title="Machine Created — What's Next?"
        open={postCreateId !== null && !associateTasksOpen}
        footer={null}
        onCancel={() => setPostCreateId(null)}
        width={420}
      >
        <p className="mb-4 text-sm text-gray-600">
          Your new machine has been created. Would you like to set it up now?
        </p>
        <Space direction="vertical" className="w-full" size="middle">
          <Button
            block
            type="primary"
            icon={<CalendarOutlined />}
            onClick={() => {
              const id = postCreateId;
              setPostCreateId(null);
              setSelectedMachineId(id);
            }}
          >
            Add Schedule
          </Button>
          <Button
            block
            onClick={() => {
              setSelectedTaskIds([]);
              setAssociateTasksOpen(true);
            }}
          >
            Associate Tasks
          </Button>
          <Button block type="default" onClick={() => setPostCreateId(null)}>
            Done
          </Button>
        </Space>
      </Modal>

      {/* Associate Tasks Modal */}
      <Modal
        title="Associate Tasks with Machine"
        open={associateTasksOpen}
        onCancel={() => setAssociateTasksOpen(false)}
        okText="Associate"
        okButtonProps={{ disabled: selectedTaskIds.length === 0, loading: associating }}
        onOk={async () => {
          if (!postCreateId || selectedTaskIds.length === 0) return;
          setAssociating(true);
          try {
            await Promise.all(
              selectedTaskIds.map((taskId) => {
                const task = allTasks.find((t: any) => t.id === taskId);
                const existingMachines = task?.machines || [];
                return updateTask({
                  id: taskId,
                  machines: [...new Set([...existingMachines, postCreateId])],
                }).unwrap();
              })
            );
            message.success(`Machine associated with ${selectedTaskIds.length} task(s)`);
            setAssociateTasksOpen(false);
          } catch {
            message.error("Failed to associate some tasks");
          } finally {
            setAssociating(false);
          }
        }}
        width={500}
      >
        <p className="mb-3 text-sm text-gray-500">
          Select tasks that this machine should be able to perform.
        </p>
        <Select
          mode="multiple"
          value={selectedTaskIds}
          onChange={setSelectedTaskIds}
          options={allTasks.map((t: any) => ({
            value: t.id,
            label: `[${t.task_id}] ${t.name}`,
          }))}
          placeholder="Select tasks..."
          showSearch
          filterOption={(input, option) =>
            (option?.label?.toString() || "").toLowerCase().includes(input.toLowerCase())
          }
          className="w-full"
        />
      </Modal>

      {/* Bulk Update Modal */}
      <Modal
        title={`Bulk Update: ${bulkField ? BULK_FIELD_LABELS[bulkField] : ""}`}
        open={bulkField !== null}
        onCancel={() => { setBulkField(null); setBulkValue(null); }}
        onOk={handleBulkApply}
        okButtonProps={{ disabled: bulkValue === null, loading: bulkLoading }}
        okText={`Apply to ${selectedRowKeys.length} machine(s)`}
        width={400}
      >
        <p className="mb-3 text-sm text-gray-500">
          Set a new value for <strong>{bulkField ? BULK_FIELD_LABELS[bulkField] : ""}</strong> on
          the {selectedRowKeys.length} selected machine(s).
        </p>
        <InputNumber
          className="w-full"
          min={0}
          max={bulkField === "max_unattended_hours" ? 168 : 100}
          step={bulkField === "max_unattended_hours" ? 1 : 5}
          value={bulkValue}
          onChange={(v) => setBulkValue(v)}
          addonAfter={bulkField !== "max_unattended_hours" ? "%" : "hrs"}
          placeholder="Enter new value..."
          autoFocus
        />
      </Modal>
    </div>
  );
}
